import React from 'react';
import type { CustomActionResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { methodBadgeStyle } from './toolStyles';

/**
 * Card de ferramenta (view "Cards"). Padrão-lista (F34): SEM checkbox — seleção
 * em massa vive só na view de tabela. Menu ••• de ações + switch inline de
 * status (Switch shared sm — F47). Ícone fixo (ferramentas não têm avatar).
 */
export interface ToolCardProps {
  action: CustomActionResponse;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (action: CustomActionResponse) => void;
  onViewLogs?: (action: CustomActionResponse) => void;
}

export const ToolCard: React.FC<ToolCardProps> = ({
  action,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewLogs,
}) => {
  const isActive = action.status === 'active';
  const method = action.endpoint.method;

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(action.id) });
  if (onViewLogs) actions.push({ label: 'Logs de Requisições', icon: 'file-text', onClick: () => onViewLogs(action) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'play',
      onClick: () => onToggleStatus(action),
    });
  }
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(action.id), variant: 'danger' });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`tool-card-${action.id}`}
    >
      {/* Header: ícone + nome + menu ••• */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: '#0ea5e9', color: '#ffffff' }}
        >
          <Icon name="zap" className="w-5 h-5" />
        </div>
        <h3 className="font-bold text-sm truncate flex-1 min-w-0" style={{ color: 'var(--theme-card-text)' }}>
          {action.displayName}
        </h3>
        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`tool-card-menu-${action.id}`} />
          </div>
        )}
      </div>

      {/* Descrição */}
      <p
        className="mt-2.5 text-xs leading-relaxed line-clamp-2"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        {action.description || 'Sem descrição'}
      </p>

      {/* Footer: método + endpoint */}
      <div
        className="mt-3 pt-3 flex items-center gap-2 min-w-0"
        style={{ borderTop: '1px solid var(--theme-card-border)' }}
      >
        <span
          className="inline-flex items-center h-[22px] px-2 rounded-md text-[11px] font-extrabold tracking-wide flex-none"
          style={methodBadgeStyle(method)}
        >
          {method}
        </span>
        <span
          className="text-xs font-mono truncate min-w-0 flex-1"
          style={{ color: 'var(--theme-card-text)' }}
          title={action.endpoint.url}
        >
          {action.endpoint.url}
        </span>
      </div>

      {/* Status — F47: switch inline (Switch shared sm) + rótulo do estado */}
      <div className="mt-2.5">
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`tool-toggle-${action.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(action)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativa' : 'Inativa'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativa' : 'Inativa'}
            data-testid={`tool-status-${action.id}`}
          />
        )}
      </div>
    </div>
  );
};

ToolCard.displayName = 'ToolCard';
